import { useState, useEffect, useCallback, useMemo } from 'react';
import { DropzoneFile } from '@lendica/components';
import { BillUploadManager, BillProcessingState } from '../services/BillUploadManager';
import { usePayLaterApi } from '../payLaterAPI';

export type FileStatus =
    | 'uploading' // File doesn't have key yet
    | 'upload-success' // File has key but not yet processed
    | 'processing' // File is being processed (pending or processing)
    | 'processing-success' // Processing completed successfully
    | 'processing-error'; // Processing failed

export interface OverallStatus {
    totalFiles: number;
    processing: number;
    completed: number;
    failed: number;
    allCompleted: boolean;
    hasErrors: boolean;
    isProcessing: boolean;
}

/**
 * Hook to get simplified status for a single file
 */
export function useFileStatus(file: DropzoneFile): {
    status: FileStatus;
    error?: string;
    billId?: string;
} {
    const [processingState, setProcessingState] = useState<BillProcessingState | undefined>(
        file.key ? BillUploadManager.getState(file.key) : undefined
    );

    useEffect(() => {
        if (!file.key) return;

        const handleUpdate = () => {
            setProcessingState(BillUploadManager.getState(file.key!));
        };

        BillUploadManager.on('state-update', handleUpdate);

        return () => {
            BillUploadManager.off('state-update', handleUpdate);
        };
    }, [file.key]);

    // Determine simplified status
    if (!file.key) {
        return { status: 'uploading' };
    }

    if (!processingState) {
        return { status: 'upload-success' };
    }

    switch (processingState.status) {
        case 'pending':
        case 'processing':
            return { status: 'processing' };
        case 'completed':
            return {
                status: 'processing-success',
                billId: processingState.billId,
            };
        case 'error':
            return {
                status: 'processing-error',
                error: processingState.error,
            };
        default:
            return { status: 'upload-success' };
    }
}

/**
 * Main hook for bill processing
 */
export function useBillProcessing() {
    const api = usePayLaterApi();
    // Configure processor on mount or when api changes
    useEffect(() => {
        BillUploadManager.setProcessBill(api.processBill);
    }, [api]);

    const [states, setStates] = useState<Map<string, BillProcessingState>>(
        BillUploadManager.getAllStates()
    );

    useEffect(() => {
        const handleUpdate = () => {
            setStates(BillUploadManager.getAllStates());
        };

        BillUploadManager.on('state-update', handleUpdate);

        return () => {
            BillUploadManager.off('state-update', handleUpdate);
        };
    }, []);

    const startProcessing = useCallback((file: DropzoneFile) => {
        BillUploadManager.startProcessing(file);
    }, []);

    const getState = useCallback((fileKey: string) => {
        return BillUploadManager.getState(fileKey);
    }, []);

    const clearCompleted = useCallback(() => {
        BillUploadManager.clearCompleted();
    }, []);

    // Calculate overall status
    const overallStatus = useMemo<OverallStatus>(() => {
        const statusCounts = {
            processing: 0,
            completed: 0,
            failed: 0,
        };

        states.forEach(state => {
            switch (state.status) {
                case 'pending':
                case 'processing':
                    statusCounts.processing++;
                    break;
                case 'completed':
                    statusCounts.completed++;
                    break;
                case 'error':
                    statusCounts.failed++;
                    break;
            }
        });

        const totalFiles = states.size;
        const allCompleted = totalFiles > 0 && statusCounts.processing === 0;
        const hasErrors = statusCounts.failed > 0;
        const isProcessing = statusCounts.processing > 0;

        return {
            totalFiles,
            processing: statusCounts.processing,
            completed: statusCounts.completed,
            failed: statusCounts.failed,
            allCompleted,
            hasErrors,
            isProcessing,
        };
    }, [states]);

    return {
        states,
        overallStatus,
        startProcessing,
        getState,
        clearCompleted,
    };
}

/**
 * Helper function to get dialog message based on overall status
 */
export function getProcessingMessage(overallStatus: OverallStatus): string {
    const { totalFiles, processing, completed, failed, allCompleted, hasErrors, isProcessing } =
        overallStatus;

    if (totalFiles === 0) {
        return 'No files to process';
    }

    if (isProcessing) {
        return `Processing ${processing} of ${totalFiles} files...`;
    }

    if (allCompleted && !hasErrors) {
        return `Successfully processed all ${completed} files!`;
    }

    if (allCompleted && hasErrors) {
        return `Completed: ${completed} successful, ${failed} failed`;
    }

    return `Status: ${completed} completed, ${failed} failed, ${processing} processing`;
}
